import { User, UserRole } from './types';
import { supabase } from './supabase';

export const createUser = async (email: string, password: string, name: string): Promise<User> => {
  try {
    console.log('Creating new user:', email);
    
    // First check if user exists - use case-insensitive comparison and maybeSingle
    const { data: existingUser, error: checkError } = await supabase
      .from('users')
      .select('id')
      .ilike('email', email.trim())
      .maybeSingle();

    if (checkError) {
      throw checkError;
    }

    if (existingUser) {
      throw new Error('Un compte existe déjà avec cet email');
    }
    
    // Create the auth user
    const { data, error: authError } = await supabase.auth.signUp({
      email: email.trim(),
      password: password,
      options: {
        data: {
          name: name // Store name in auth metadata
        }
      }
    });

    if (authError) {
      console.error('Auth error:', authError);
      if (authError.message.includes('User already registered') || 
          authError.message.includes('user_already_exists')) {
        throw new Error('Un compte existe déjà avec cet email');
      }
      if (authError.message.includes('Password')) {
        throw new Error('Le mot de passe doit contenir au moins 8 caractères');
      }
      throw new Error(authError.message || 'Erreur lors de la création du compte');
    }

    if (!data.user) {
      throw new Error('Erreur lors de la création du compte: Aucun utilisateur créé');
    }

    console.log('Auth user created:', data.user.id);

    // Then create the user profile in the public schema
    const { data: profileData, error: profileError } = await supabase
      .from('users')
      .insert([{
        id: data.user.id,
        email: email.trim(),
        name,
        role: 'trial',
        remaining_credits: 5,
        created_at: new Date().toISOString()
      }])
      .select('*')
      .maybeSingle();

    if (profileError) {
      console.error('Profile creation error:', profileError);
      // Clean up the auth user if profile creation fails
      await supabase.auth.admin.deleteUser(data.user.id);
      throw new Error('Erreur lors de la création du profil utilisateur');
    }

    if (!profileData) {
      throw new Error('Profil utilisateur non créé');
    }

    console.log('User profile created:', profileData.id);

    // Create user object
    const user: User = {
      id: profileData.id,
      email: profileData.email,
      name: profileData.name,
      role: profileData.role,
      remainingCredits: profileData.remaining_credits,
      createdAt: profileData.created_at,
      password // We don't store this in DB, just for session
    };

    return user;
  } catch (error: any) {
    console.error('User creation error:', error);
    // Pass through our custom error messages
    if (error.message === 'Un compte existe déjà avec cet email' ||
        error.message === 'Le mot de passe doit contenir au moins 8 caractères' ||
        error.message === 'Erreur lors de la création du profil utilisateur') {
      throw error;
    }
    throw new Error(error.message || 'Erreur lors de la création du compte');
  }
};

export const loginUser = async (email: string, password: string): Promise<User> => {
  try {
    // Validate input
    if (!email || !password) {
      throw new Error('Email et mot de passe requis');
    }

    // Trim email and ensure it's lowercase
    const cleanEmail = email.trim().toLowerCase();

    // Attempt to sign in
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email: cleanEmail,
      password: password,
    });

    if (authError) {
      console.error('Auth error:', authError);
      // Provide more specific error messages
      if (authError.message.includes('Invalid login credentials')) {
        throw new Error('Email ou mot de passe incorrect');
      }
      if (authError.message.includes('Email not confirmed')) {
        throw new Error('Veuillez confirmer votre email avant de vous connecter');
      }
      throw new Error(authError.message || 'Erreur lors de la connexion');
    }

    if (!authData.user) {
      throw new Error('Erreur lors de la connexion: Aucun utilisateur trouvé');
    }

    // Fetch user profile from public schema
    const { data: profile, error: profileError } = await supabase
      .from('users')
      .select('*')
      .eq('id', authData.user.id)
      .maybeSingle();

    if (profileError) {
      console.error('Profile fetch error:', profileError);
      throw new Error('Erreur lors de la récupération du profil');
    }

    if (!profile) {
      throw new Error('Profil utilisateur non trouvé');
    }

    // Update last login timestamp
    const { error: updateError } = await supabase
      .from('users')
      .update({ last_login: new Date().toISOString() })
      .eq('id', authData.user.id);

    if (updateError) {
      console.error('Error updating last login:', updateError);
      // Don't throw here as it's not critical
    }

    return {
      id: profile.id,
      email: profile.email,
      name: profile.name,
      role: profile.role,
      remainingCredits: profile.remaining_credits,
      createdAt: profile.created_at,
      lastLogin: profile.last_login,
      password // We don't store this in DB, just for session
    };
  } catch (error: any) {
    console.error('Login error:', error);
    if (error.message === 'Failed to fetch') {
      throw new Error('Erreur de connexion au serveur. Veuillez réessayer.');
    }
    // Pass through our custom error messages
    if (error.message === 'Email ou mot de passe incorrect' ||
        error.message === 'Aucun compte trouvé avec cet email' ||
        error.message === 'Veuillez confirmer votre email avant de vous connecter') {
      throw error;
    }
    throw new Error('Erreur lors de la connexion');
  }
};

export const getCurrentUser = async (): Promise<User | null> => {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('Session error:', sessionError);
      return null;
    }

    if (!session?.user?.id) {
      console.log('No active session found');
      return null;
    }

    const { data: profile, error: profileError } = await supabase
      .from('users')
      .select('*')
      .eq('id', session.user.id)
      .maybeSingle();

    if (profileError) {
      console.error('Profile fetch error:', profileError);
      return null;
    }

    // If no profile is found, return null instead of throwing an error
    if (!profile) {
      console.log('No user profile found for ID:', session.user.id);
      return null;
    }

    // Only return the user object if we have all required fields
    if (!profile.id || !profile.email) {
      console.error('Invalid profile data:', profile);
      return null;
    }

    return {
      id: profile.id,
      email: profile.email,
      name: profile.name || null,
      role: profile.role,
      remainingCredits: profile.remaining_credits,
      createdAt: profile.created_at,
      lastLogin: profile.last_login,
      password: '' // We don't store or return passwords
    };
  } catch (error) {
    console.error('Get current user error:', error);
    return null;
  }
};

export const logoutUser = async (): Promise<void> => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

export const decrementCredits = async (userId: string): Promise<void> => {
  if (!userId) {
    throw new Error('ID utilisateur requis pour décrémenter les crédits');
  }

  try {
    // Récupérer la valeur actuelle des crédits
    const { data, error: fetchError } = await supabase
      .from('users')
      .select('remaining_credits')
      .eq('id', userId)
      .maybeSingle();

    if (fetchError) throw fetchError;

    if (data && data.remaining_credits > 0) {
      // Décrémenter les crédits de 1
      const { error: updateError } = await supabase
        .from('users')
        .update({ remaining_credits: data.remaining_credits - 1 })
        .eq('id', userId);

      if (updateError) throw updateError;
    } else {
      throw new Error('Crédits insuffisants ou non trouvés');
    }
  } catch (error) {
    console.error('Error decrementing credits:', error);
    throw new Error('Impossible de décrémenter les crédits');
  }
};

export const hasRemainingCredits = async (userId: string): Promise<boolean> => {
  if (!userId) {
    console.error('No user ID provided to check credits');
    return false;
  }

  try {
    const { data, error } = await supabase
      .from('users')
      .select('remaining_credits, role')
      .eq('id', userId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return false;
    
    // Infinite credits for pro and enterprise users
    if (['pro', 'entreprise', 'admin'].includes(data.role)) {
      return true;
    }

    return data.remaining_credits > 0;
  } catch (error) {
    console.error('Error checking credits:', error);
    return false;
  }
};

export const saveJobPost = async (userId: string, jobPost: any): Promise<void> => {
  if (!userId) {
    throw new Error('ID utilisateur requis pour sauvegarder le post');
  }

  try {
    const { error } = await supabase
      .from('job_posts')
      .insert([{
        user_id: userId,
        title: jobPost.title,
        company: jobPost.company,
        content: jobPost.content,
        created_at: new Date().toISOString()
      }]);

    if (error) throw error;
  } catch (error) {
    console.error('Error saving job post:', error);
    throw new Error('Erreur lors de la sauvegarde du job post');
  }
};

export const getSavedPosts = async (userId: string): Promise<any[]> => {
  try {
    const { data, error } = await supabase
      .from('job_posts')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching saved posts:', error);
    throw new Error('Erreur lors de la récupération des posts sauvegardés');
  }
};

export const getSubscriptionInfo = async (userId?: string): Promise<{ role: string, remainingCredits: number }> => {
  try {
    if (!userId) {
      console.warn('UserID is undefined, returning default subscription info');
      return {
        role: 'trial',
        remainingCredits: 0
      };
    }

    const { data, error } = await supabase
      .from('users')
      .select('role, remaining_credits')
      .eq('id', userId)
      .maybeSingle();

    if (error) throw error;
    if (!data) {
      console.warn('No subscription info found for user:', userId);
      return {
        role: 'trial',
        remainingCredits: 0
      };
    }

    return {
      role: data.role,
      remainingCredits: data.remaining_credits
    };
  } catch (error) {
    console.error('Error fetching subscription info:', error);
    throw new Error("Impossible de récupérer les informations d'abonnement");
  }
};

export const hasProAccess = async (userId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('role')
      .eq('id', userId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return false;
    
    return ['pro', 'entreprise', 'admin'].includes(data.role);
  } catch (error) {
    console.error('Error checking pro access:', error);
    return false;
  }
};